import * as THREE from 'three';
import { Renderer } from './scene/renderer';
import { Minimap } from './scene/minimap';
import { PerfMonitor } from './scene/perf';
import { UnitPool, type UnitFrame } from './entities/unit';
import { BuildingRenderer, LakeRenderer, MarkerPool, ResourceRenderer, TracerPool, bldSize, resColor, wallMask, wallVariant } from './entities/world_meshes';
import { GameSocket, type ConnStatus } from './network/socket';
import { sfx } from './audio/sound';
import { HUD } from './ui/hud';
import type { Building, ResNode, RoomInfo, ServerMsg, Snapshot, Unit } from './network/protocol';
import { BARRACKS_LEVELS, BLD_COSTS, GATE_LEVEL_NAMES, TOWER_LEVELS, TRAIN_COSTS, WALL_LEVELS } from './network/protocol';

const INTERP_MS = 120;
const MINIMAP_MS = 100;

interface Stamped {
  snap: Snapshot;
  at: number; // client clock (performance.now)
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// Game ties networking, rendering, and input together:
//   - snapshots arrive at 10 Hz; units render interpolated 120 ms behind,
//   - buildings/resources render from the latest snapshot,
//   - all orders go through the authoritative server (no client prediction).
export class Game {
  private renderer: Renderer;
  private units: UnitPool;
  private buildings: BuildingRenderer;
  private resources: ResourceRenderer;
  private lakes: LakeRenderer;
  private tracers: TracerPool;
  private markers: MarkerPool;
  private minimap: Minimap;
  private hud: HUD;
  private socket = new GameSocket();

  private name = localStorage.getItem('bharat-name') || '';
  private rooms: RoomInfo[] = [];
  private roomCode = '';
  private roomPlayers: string[] = [];
  private roomSeats = 2;
  private roomDifficulty = 'medium';
  private roomRichness = 'normal';
  private roomBots: string[] = [];
  private myPid = -1;
  private isOwner = false;
  private playing = false;
  private over = false;
  private connStatus: ConnStatus = 'connecting';
  private linkDown = false; // socket dropped mid-game, waiting to rejoin

  private prev: Stamped | null = null;
  private next: Stamped | null = null;
  private camInit = false;

  private selected = new Set<number>();
  private selectedBld: number | null = null;
  private selectedNode: number | null = null; // inspected resource node
  private selHasVill = false; // selection includes a villager (hover preview)
  private enemySel: { id: number; bld: boolean } | null = null;
  private placement: string | null = null;
  private ghost: THREE.Group | null = null;
  private ghostMask = 0; // corner variant baked into the live ghost
  private amoveArmed = false;
  // Wall line-drag: click sets the rampart start, drag extends the line,
  // release builds every segment. Null when not dragging.
  private wallDrag: { sx: number; sy: number; wx: number; wz: number } | null = null;
  private wallGhosts: THREE.Group[] = [];
  private static readonly MAX_WALL_LINE = 16; // segments per drag (10🪵 each)

  private boxEl: HTMLDivElement;
  private boxStart: { sx: number; sy: number } | null = null;
  private panning: { sx: number; sy: number } | null = null;
  private proj = new THREE.Vector3(); // reused screen-space projection temp
  private lastFrame = performance.now();
  private hudTimer = 0;
  private perf = new PerfMonitor();
  private lastStaticTick = -1;
  private lastMinimapAt = 0;
  private hoverLast = 0;
  // Reused per-frame scratch to avoid Map/array churn at 60fps.
  private oldById = new Map<number, Unit>();
  private framesCache: UnitFrame[] = [];

  constructor(container: HTMLElement) {
    this.renderer = new Renderer(container);
    this.units = new UnitPool(this.renderer.scene);
    this.buildings = new BuildingRenderer(this.renderer.scene);
    this.resources = new ResourceRenderer(this.renderer.scene);
    this.lakes = new LakeRenderer(this.renderer.scene);
    this.tracers = new TracerPool(this.renderer.scene);
    this.markers = new MarkerPool(this.renderer.scene);
    this.minimap = new Minimap(128); // floats bottom-right above the bar
    this.hud = new HUD(container, {
      getName: () => this.name,
      setName: (n) => {
        this.name = n.trim().slice(0, 16);
        localStorage.setItem('bharat-name', this.name);
      },
      onCreate: (bots, diff, rich) => this.socket.create(this.name || 'Guest', 6, bots, diff, rich),
      onSetDiff: (diff) => this.socket.setDiff(diff),
      onSetRich: (rich) => this.socket.setRich(rich),
      onJoin: (room) => this.socket.join(room.toUpperCase(), this.name || 'Guest'),
      onRefreshRooms: () => this.socket.listRooms(),
      onRetry: () => this.socket.retryNow(),
      onAddBot: () => this.socket.addBot(),
      onStart: () => this.socket.start(),
      onLeave: () => this.requestLeave(),
      onTrain: (kind) => this.train(kind),
      onUpgrade: () => this.upgradeTower(),
      onConvert: () => this.convertWall(),
      onGatherNode: () => this.gatherSelectedNode(),
      onSelectVillagers: (scope) => this.selectVillagers(scope),
      onSelectArmy: (kind) => this.selectArmy(kind),
      onPlace: (kind) => this.setPlacement(kind),
      onStop: () => this.stop(),
      onAmove: () => {
        this.amoveArmed = !this.amoveArmed;
        this.setPlacement(null);
      },
      onToggleHelp: () => this.hud.toggleHelp(),
      onToggleFullscreen: () => this.toggleFullscreen(),
    });
    this.hud.minimapSlot.appendChild(this.minimap.canvas);
    this.minimap.canvas.addEventListener('click', (e) => {
      const w = this.minimap.toWorld(e.clientX, e.clientY);
      this.renderer.jumpTo(w.x, w.z);
    });

    this.boxEl = document.createElement('div');
    this.boxEl.style.cssText =
      'position:absolute;border:1px solid #4ade80;background:rgba(74,222,128,0.15);display:none;pointer-events:none;z-index:5';
    container.appendChild(this.boxEl);

    this.socket.onMessage((m) => this.onMsg(m));
    // Show the lobby immediately (with connection state) so a dead server
    // reads as an error, never as an empty field.
    this.hud.showMenu(this.rooms, this.connStatus);
    this.socket.onStatus((s) => this.onStatus(s));
    this.socket.connect();
    this.attachInput(container);
    requestAnimationFrame((t) => this.frame(t));
  }

  // ---- networking ----

  private onStatus(s: ConnStatus): void {
    this.connStatus = s;
    if (!this.playing) {
      this.hud.showMenu(this.rooms, s);
      if (s === 'open') this.socket.listRooms();
      return;
    }
    if (s === 'closed' && !this.over) {
      this.linkDown = true;
      this.hud.toast('🔌 Connection lost — reconnecting…');
    } else if (s === 'open' && this.linkDown && !this.over && this.roomCode) {
      // Rejoin the running match; the server reclaims our ghosted seat when
      // it can verify us (same signed session), else it says room is full.
      this.socket.join(this.roomCode, this.name || 'Guest');
    }
  }

  private onMsg(m: ServerMsg): void {
    switch (m.t) {
      case 'rooms':
        this.rooms = m.rooms;
        if (!this.playing) this.hud.showMenu(this.rooms, this.connStatus);
        break;
      case 'joined':
        this.roomCode = m.room;
        this.myPid = m.pid ?? 0; // server always sends pid; default guards old builds
        this.isOwner = this.myPid === 0;
        break;
      case 'lobby':
        this.roomPlayers = m.players ?? [];
        this.roomSeats = m.seats ?? this.roomPlayers.length;
        this.roomDifficulty = m.difficulty || 'medium';
        this.roomRichness = m.richness || 'normal';
        this.roomBots = m.bots ?? [];
        if (!this.playing) {
          this.hud.showRoom(this.roomCode, this.roomPlayers, this.roomSeats, this.isOwner, !!m.started, this.roomDifficulty, this.roomRichness, this.roomBots);
        } else if (
          this.linkDown && m.started && m.room === this.roomCode && !this.over
        ) {
          // Reclaim path: snapshots resume flowing to our (reclaimed) seat.
          this.linkDown = false;
          this.prev = this.next = null;
          this.camInit = true; // keep camera where it was
          this.hud.enterGame(this.roomCode, this.isOwner);
          this.hud.toast('🔌 Reconnected!');
        }
        break;
      case 'event':
        if (m.event === 'start') {
          this.beginPlay();
          sfx.start();
        } else if (m.event === 'over') {
          this.endGame(parseInt(m.detail ?? '-1', 10), this.latest());
        } else if (m.event === 'left') {
          // A rival surrendered mid-game: their city is now bot-run.
          this.hud.toast(`🏳️ ${m.detail ?? 'A player'} left — a bot takes over.`);
        } else if (m.event === 'error') {
          this.hud.toast(m.detail ?? ' error');
          sfx.error();
        }
        break;
      case 'error':
        this.hud.toast(m.msg);
        sfx.error();
        break;
      case 'state':
        this.onSnapshot(m.snap);
        break;
    }
  }

  private beginPlay(): void {
    this.playing = true;
    this.over = false;
    this.linkDown = false;
    this.prev = this.next = null;
    this.lastStaticTick = -1;
    this.lastMinimapAt = 0;
    this.camInit = false;
    this.selected.clear();
    this.selectedBld = null;
    this.selectedNode = null;
    this.selHasVill = false;
    this.enemySel = null;
    this.amoveArmed = false;
    this.setPlacement(null);
    this.markers.clear();
    this.tracers.clear();
    this.units.clear();
    this.buildings.clear();
    this.resources.clear();
    this.lakes.clear();
    this.hud.clearToasts();
    this.hud.enterGame(this.roomCode, this.isOwner);
    this.hud.toast('⚔️ Battle begins! Guard your Town Center — lose it and you fall.');
  }

  private endGame(winner: number, snap: Snapshot | null): void {
    if (this.over) return;
    this.over = true;
    this.renderer.pointerEdge = null; // overlay covers the field: stop panning
    if (this.ghost) this.ghost.visible = false;
    this.markers.clearHover();
    const draw = winner === -2;
    const won = winner === this.myPid;
    if (draw) sfx.complete();
    else if (won) sfx.win();
    else sfx.lose();
    this.hud.gameOver(won, draw, snap ?? this.latest(), this.myPid, winner);
  }

  // Mid-game leave is a surrender: the server hands the city to a bot so
  // the rest can continue (and ends the match when only bots remain).
  private requestLeave(): void {
    if (this.playing && !this.over) {
      const ok = window.confirm(
        'Leave this match? Your city will be handed to a bot so the others can continue.',
      );
      if (!ok) return;
      this.hud.toast('🏳️ You left — a bot takes over your city.');
    }
    this.leaveToLobby();
  }

  private leaveToLobby(): void {
    this.socket.leave();
    this.playing = false;
    this.over = false;
    this.linkDown = false;
    this.prev = this.next = null;
    this.myPid = -1;
    this.roomCode = '';
    this.selected.clear();
    this.selectedBld = null;
    this.selectedNode = null;
    this.selHasVill = false;
    this.enemySel = null;
    this.amoveArmed = false;
    this.setPlacement(null);
    this.markers.clear();
    this.tracers.clear();
    this.renderer.pointerEdge = null;
    this.renderer.edgePaused = false;
    this.hud.showMenu(this.rooms, this.connStatus);
    this.socket.listRooms();
  }

  private onSnapshot(snap: Snapshot): void {
    const now = performance.now();
    this.prev = this.next;
    this.next = { snap, at: now };
    if (!this.prev) this.prev = this.next;
    this.tracers.spawn(snap.sh ?? []);
    // Construction finished / my unit emerged since the last snapshot.
    const psnap = this.prev.snap;
    if (psnap !== snap) {
      const wasProg = new Map(psnap.bd.map((b) => [b.id, b.p]));
      for (const b of snap.bd) {
        const was = wasProg.get(b.id);
        if (was !== undefined && was < 1 && b.p >= 1) {
          sfx.complete();
          break;
        }
      }
      const wasU = new Set(psnap.u.map((u) => u.id));
      for (const u of snap.u) {
        if (u.o === this.myPid && !wasU.has(u.id)) {
          sfx.emerge();
          break;
        }
      }
      // Upgrade research finished since the last snapshot.
      const wasLvl = new Map(psnap.bd.map((b) => [b.id, b.lv ?? 0]));
      for (const b of snap.bd) {
        const was = wasLvl.get(b.id);
        const now = b.lv ?? 0;
        if (was === undefined || now <= was || b.o !== this.myPid) continue;
        const tag = b.k === 'tower'
          ? `${TOWER_LEVELS[now]?.icon ?? ''} ${TOWER_LEVELS[now]?.name ?? ''}`
          : b.k === 'barracks'
            ? `${BARRACKS_LEVELS[now]?.icon ?? ''} ${BARRACKS_LEVELS[now]?.name ?? ''}`
            : b.k === 'wall'
              ? `${WALL_LEVELS[now]?.icon ?? ''} ${WALL_LEVELS[now]?.name ?? ''}`
              : b.k === 'gate'
                ? `🛡️ ${GATE_LEVEL_NAMES[Math.min(now, GATE_LEVEL_NAMES.length - 1)] ?? ''}`
                : null;
        if (tag) {
          sfx.complete();
          this.hud.toast(`⬆️ ${tag} ready!`);
        }
      }
      // Wall → gate conversions landing since the last snapshot.
      const wasKind = new Map(psnap.bd.map((b) => [b.id, b.k]));
      for (const b of snap.bd) {
        if (wasKind.get(b.id) === 'wall' && b.k === 'gate' && b.o === this.myPid) {
          sfx.complete();
          this.hud.toast('🛡️ Gate ready — friendlies pass, enemies knock.');
        }
      }
    }
    // Prune selections of dead entities.
    const aliveU = new Set(snap.u.map((u) => u.id));
    for (const id of this.selected) if (!aliveU.has(id)) this.selected.delete(id);
    if (this.selectedBld != null && !snap.bd.some((b) => b.id === this.selectedBld)) {
      this.selectedBld = null;
    }
    // Drop the inspected node once it is gathered out.
    if (this.selectedNode != null && !snap.nd.some((n) => n.id === this.selectedNode)) {
      this.selectedNode = null;
      this.refreshPanel(snap);
    }
    if (!this.camInit) {
      const tc = snap.bd.find((b) => b.o === this.myPid && b.k === 'townCenter');
      if (tc) {
        this.renderer.jumpTo(tc.x, tc.z);
        this.camInit = true;
      }
    }
    if (snap.win !== -1) this.endGame(snap.win, snap);
  }

  private latest(): Snapshot | null {
    return this.next?.snap ?? null;
  }

  private me(): Snapshot['pl'][number] | null {
    const s = this.latest();
    return s?.pl.find((p) => p.id === this.myPid) ?? null;
  }

  // ---- per-frame ----

  private frame(now: number): void {
    const dt = Math.min((now - this.lastFrame) / 1000, 0.1);
    this.lastFrame = now;
    // Adaptive resolution governor: steps pixelRatio down on sustained
    // hitches, back up when headroom returns. -1 = unchanged (no GL work).
    const scale = this.perf.update(dt);
    if (scale > 0) this.renderer.setRenderScale(scale);
    if (this.playing && this.next) {
      this.renderer.update(dt);
      this.renderWorld(now, dt);
    }
    this.renderer.render();
    requestAnimationFrame((t) => this.frame(t));
  }

  private renderWorld(now: number, dt: number): void {
    const renderAt = now - INTERP_MS;
    const a = this.prev!;
    const b = this.next!;
    let alpha = 1;
    if (a !== b) {
      const span = Math.max(b.at - a.at, 1);
      alpha = Math.min(Math.max((renderAt - a.at) / span, 0), 1);
    }
    // Static scene (resources/lakes) only changes at 10Hz snapshot ticks —
    // skip re-baking it every 60fps frame. Buildings still sync every frame
    // for billboarded HP bars, but group traverse is now cheap (merged) and
    // skips opacity work unless construction state flips.
    const staticDirty = b.snap.tick !== this.lastStaticTick;
    if (staticDirty) this.lastStaticTick = b.snap.tick;
    // Reuse scratch containers (no per-frame Map/array allocation).
    this.oldById.clear();
    for (const u of a.snap.u) this.oldById.set(u.id, u);
    const frames = this.framesCache;
    frames.length = 0;
    for (const u of b.snap.u) {
      const o = this.oldById.get(u.id);
      frames.push({
        id: u.id,
        kind: u.k,
        o: u.o,
        x: o ? lerp(o.x, u.x, alpha) : u.x,
        z: o ? lerp(o.z, u.z, alpha) : u.z,
        hpFrac: u.hp / u.m,
        selected: this.selected.has(u.id),
        moving: u.st === 'move' || u.st === 'amove' || u.st === 'gather' || u.st === 'ret' || u.st === 'attack',
        busy: u.st === 'gather' || u.st === 'build' || u.st === 'attack',
        // Head-load: villagers visibly carry goods on the trip home.
        carry: u.k === 'villager' && u.st === 'ret' && (u.cn ?? 0) > 0 ? (u.ca ?? '') : '',
        carryFrac: Math.min(1, (u.cn ?? 0) / 15),
      });
    }
    this.units.sync(frames, this.renderer.camera, dt, { lowDetail: this.perf.lowDetail });
    this.buildings.sync(b.snap.bd, this.renderer.camera, frames, dt);
    if (staticDirty) {
      this.resources.sync(b.snap.nd);
      this.lakes.sync(b.snap.lk ?? []);
    }
    this.tracers.update(dt);
    this.markers.update(dt);

    if (staticDirty || now - this.lastMinimapAt >= MINIMAP_MS) {
      this.lastMinimapAt = now;
      this.minimap.draw(b.snap, {
        x: this.renderer.camX,
        z: this.renderer.camZ,
        w: this.renderer.camDist * 1.1,
      });
    }

    this.hudTimer -= dt;
    if (this.hudTimer <= 0) {
      this.hudTimer = 0.25;
      const me = this.me();
      const others = (b.snap.pl ?? []).filter((p) => p.id !== this.myPid);
      if (me) this.hud.setTop(me, others, this.roomCode);
      this.refreshPanel(b.snap);
    }
  }

  private refreshPanel(snap?: Snapshot): void {
    if (!this.playing) return;
    const s = snap ?? this.latest();
    if (!s) return;
    const units = [...this.selected]
      .map((id) => s.u.find((u) => u.id === id))
      .filter((u): u is Unit => !!u);
    this.selHasVill = units.some((u) => u.k === 'villager');
    const bld = s.bd.find((x) => x.id === this.selectedBld) ?? null;
    let enemy: Unit | Building | null = null;
    if (this.enemySel && units.length === 0 && !bld) {
      enemy = this.enemySel.bld
        ? (s.bd.find((x) => x.id === this.enemySel!.id) ?? null)
        : (s.u.find((x) => x.id === this.enemySel!.id) ?? null);
      if (enemy && (enemy as Unit).o === this.myPid) enemy = null;
    } else {
      this.enemySel = null;
    }
    // Inspected resource: shown only when nothing else is selected.
    let node: ResNode | null = null;
    if (this.selectedNode != null && units.length === 0 && !bld && !enemy) {
      node = s.nd.find((n) => n.id === this.selectedNode) ?? null;
      if (!node) this.selectedNode = null;
    } else if (this.selectedNode != null && (units.length > 0 || bld || enemy)) {
      this.selectedNode = null;
    }
    // Village overview for the persistent bottom-bar row.
    const KIND_ORDER = ['swordsman', 'spearman', 'archer', 'rajput'];
    const mine = s.u.filter((u) => u.o === this.myPid);
    const vills = mine.filter((u) => u.k === 'villager');
    const armyMap = new Map<string, number>();
    for (const u of mine) {
      if (u.k !== 'villager') armyMap.set(u.k, (armyMap.get(u.k) ?? 0) + 1);
    }
    const army = [...armyMap.entries()]
      .sort((a, b) => KIND_ORDER.indexOf(a[0]) - KIND_ORDER.indexOf(b[0]))
      .map(([kind, n]) => ({ kind, n }));
    const overview = {
      villagers: vills.length,
      idle: vills.filter((u) => u.st === 'idle').length,
      army,
      armyTotal: army.reduce((t, a) => t + a.n, 0),
    };
    this.hud.setPanel({
      units,
      bld: bld && bld.o === this.myPid ? bld : null,
      me: this.me(),
      placement: this.placement,
      amoveArmed: this.amoveArmed,
      enemy,
      node,
      overview,
      snap: s,
    });
  }

  // ---- fullscreen ----

  private toggleFullscreen(): void {
    try {
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => undefined);
      } else {
        void document.documentElement.requestFullscreen().catch(() => undefined);
      }
    } catch {
      /* fullscreen unsupported (e.g. iPhone Safari): stay windowed */
    }
  }

  // ---- placement ghost ----

  // Central switch for build-placement mode: owns the ghost preview mesh.
  private setPlacement(kind: string | null): void {
    if (this.ghost) {
      this.renderer.scene.remove(this.ghost);
      this.buildings.freeGhost(this.ghost);
      this.ghost = null;
    }
    this.clearWallLine();
    this.placement = kind;
    if (kind && this.playing) {
      this.ghost = this.buildings.makeGhost(kind, this.myPid);
      this.ghostMask = 0;
      this.ghost.visible = false;
      this.renderer.scene.add(this.ghost);
      this.amoveArmed = false;
    }
    this.refreshPanel();
  }

  // Swap the live ghost for the right corner variant when the cursor's
  // connectivity changes (blueprints are cached, so this is just a few
  // shared-geometry meshes, never a bake).
  private refreshGhost(kind: string, mask: number): void {
    const want = wallVariant(mask);
    if (!this.ghost || this.ghostMask === want) return;
    this.renderer.scene.remove(this.ghost);
    this.buildings.freeGhost(this.ghost);
    this.ghost = this.buildings.makeGhost(kind, this.myPid, mask);
    this.ghostMask = want;
    this.renderer.scene.add(this.ghost);
  }

    // Advisory validity check mirroring the server's buildable() plus
  // affordability. The server still has the final say.
  // Wall snap preview: mirrors server snapWall() exactly (nearest friendly
  // wall within 4, dominant axis, centers 2.0 apart, clamped to the map), so
  // the ghost sits where the segment will actually land.
  private snapWall(kind: string, x: number, z: number, s: Snapshot): { x: number; z: number } {
    if (kind !== 'wall') return { x, z };
    const walls = s.bd
      .filter((b) => b.o === this.myPid && (b.k === 'wall' || b.k === 'gate'))
      .map((b) => ({ x: b.x, z: b.z }));
    return this.snapWallTo(x, z, walls);
  }

  // Snap one wall cell against a wall list (snapshot walls plus already
  // planned line cells). The server snaps each build order the same way
  // against live foundations, so simulating the growing line keeps the
  // preview exactly where segments will land.
  private snapWallTo(x: number, z: number, walls: Array<{ x: number; z: number }>): { x: number; z: number } {
    let best: { x: number; z: number } | null = null;
    let bestD = 4.0;
    for (const b of walls) {
      const d = Math.hypot(x - b.x, z - b.z);
      if (d < bestD) {
        bestD = d;
        best = b;
      }
    }
    if (!best) return { x, z };
    const dx = x - best.x;
    const dz = z - best.z;
    const clampW = (v: number) => Math.min(Math.max(v, 2), 158);
    if (Math.abs(dx) > Math.abs(dz)) {
      return { x: clampW(best.x + (dx < 0 ? -2 : 2)), z: best.z };
    }
    return { x: best.x, z: clampW(best.z + (dz < 0 ? -2 : 2)) };
  }

  // Straight axis-aligned wall line from a snapped anchor toward (toX,toZ):
  // dominant axis wins, one cell every 2.0 units. Each cell re-snaps against
  // the anchor line so far, mirroring the server's sequential placement.
  private wallLineCells(fromX: number, fromZ: number, toX: number, toZ: number, s: Snapshot): Array<{ x: number; z: number }> {
    const walls = s.bd
      .filter((b) => b.o === this.myPid && (b.k === 'wall' || b.k === 'gate'))
      .map((b) => ({ x: b.x, z: b.z }));
    const start = this.snapWallTo(fromX, fromZ, walls);
    const planned: Array<{ x: number; z: number }> = [start];
    const dx = toX - start.x;
    const dz = toZ - start.z;
    const clampW = (v: number) => Math.min(Math.max(v, 2), 158);
    if (Math.abs(dx) > Math.abs(dz)) {
      const n = Math.min(Math.round(Math.abs(dx) / 2), Game.MAX_WALL_LINE - 1);
      const dir = dx < 0 ? -1 : 1;
      for (let i = 1; i <= n; i++) {
        const c = this.snapWallTo(clampW(start.x + i * 2 * dir), start.z, [...walls, ...planned]);
        planned.push(c);
      }
    } else {
      const n = Math.min(Math.round(Math.abs(dz) / 2), Game.MAX_WALL_LINE - 1);
      const dir = dz < 0 ? -1 : 1;
      for (let i = 1; i <= n; i++) {
        const c = this.snapWallTo(start.x, clampW(start.z + i * 2 * dir), [...walls, ...planned]);
        planned.push(c);
      }
    }
    return planned;
  }

  private placeBlockReason(kind: string, x: number, z: number, s: Snapshot): string | null {
    const cost = BLD_COSTS[kind];
    const me = s.pl.find((p) => p.id === this.myPid);
    if (cost && me) {
      const lacking: string[] = [];
      if (me.w < cost.w) lacking.push(`${Math.ceil(cost.w - me.w)}🪵`);
      if (me.g < cost.g) lacking.push(`${Math.ceil(cost.g - me.g)}💰`);
      if (me.f < cost.f) lacking.push(`${Math.ceil(cost.f - me.f)}🍎`);
      if (lacking.length > 0) return `Need ${lacking.join(' ')} more`;
    }
    const half = bldSize(kind) / 2 + 0.6;
    for (const b of s.bd) {
      // Walls and gates may touch each other exactly (centers 2.0 apart);
      // everything else keeps a walkable margin. Mirrors server buildable().
      const margin = (kind === 'wall' || kind === 'gate') && (b.k === 'wall' || b.k === 'gate') ? 0 : 0.6;
      const bl = bldSize(b.k) / 2 + bldSize(kind) / 2 + margin;
      if (Math.abs(b.x - x) < bl && Math.abs(b.z - z) < bl) {
        return '🚫 Blocked — too close to another building';
      }
    }
    for (const n of s.nd) {
      if (Math.abs(n.x - x) < half + 1 && Math.abs(n.z - z) < half + 1) {
        return '🚫 Blocked — resources in the way';
      }
    }
    return null;
  }

  // Drop every wall-line ghost and forget an in-progress drag.
  private clearWallLine(): void {
    this.wallDrag = null;
    for (const g of this.wallGhosts) {
      this.renderer.scene.remove(g);
      this.buildings.freeGhost(g);
    }
    this.wallGhosts = [];
  }

  // Show one ghost per planned line cell (green = builds, red = blocked),
  // each baked for its own corner variant so junctions preview sealed.
  // Ghosts are pooled across drags and rebaked only on variant flips.
  private updateWallLinePreview(cells: Array<{ x: number; z: number }>, s: Snapshot): void {
    const base = s.bd
      .filter((b) => b.o === this.myPid && (b.k === 'wall' || b.k === 'gate'))
      .map((b) => ({ x: b.x, z: b.z, o: b.o }));
    const mine = cells.map((c) => ({ ...c, o: this.myPid }));
    const all = [...base, ...mine];
    while (this.wallGhosts.length < cells.length) {
      const g = this.buildings.makeGhost('wall', this.myPid);
      g.visible = false;
      g.userData.wvar = -1;
      this.renderer.scene.add(g);
      this.wallGhosts.push(g);
    }
    cells.forEach((c, i) => {
      const mask = wallMask(c.x, c.z, all, this.myPid);
      const want = wallVariant(mask);
      let g = this.wallGhosts[i];
      if (g.userData.wvar !== want) {
        this.renderer.scene.remove(g);
        this.buildings.freeGhost(g);
        g = this.buildings.makeGhost('wall', this.myPid, mask);
        g.userData.wvar = want;
        this.renderer.scene.add(g);
        this.wallGhosts[i] = g;
      }
      g.visible = true;
      g.position.set(c.x, 0, c.z);
      g.rotation.y = 0;
      this.buildings.setGhostValid(g, this.placeBlockReason('wall', c.x, c.z, s) === null);
    });
    for (let i = cells.length; i < this.wallGhosts.length; i++) {
      this.wallGhosts[i].visible = false;
    }
  }

  // Build every valid cell in the dragged line. Selected villagers split
  // across the foundations round-robin so the whole rampart rises at once;
  // segments nobody covers wait as foundations (right-click to resume).
  private commitWallLine(cells: Array<{ x: number; z: number }>, s: Snapshot, keepPlacing: boolean): void {
    const me = s.pl.find((p) => p.id === this.myPid);
    const each = BLD_COSTS.wall.w;
    const afford = me ? Math.floor(me.w / each) : 0;
    if (afford <= 0) {
      this.hud.toast(`Need ${each}🪵 per wall — chop more wood.`);
      sfx.error();
      return;
    }
    const valid = cells.slice(0, afford).filter((c) => this.placeBlockReason('wall', c.x, c.z, s) === null);
    if (valid.length === 0) {
      const reason = this.placeBlockReason('wall', cells[0].x, cells[0].z, s)
        ?? (cells.length > afford ? `Need ${Math.ceil(cells.length * each - (me?.w ?? 0))}🪵 more for ${cells.length}× wall.` : null)
        ?? '🚫 Blocked — adjust the line and retry';
      this.hud.toast(reason); // stay in placement mode, adjust and retry
      sfx.error();
      return;
    }
    const vills = (s.u ?? [])
      .filter((u) => u.o === this.myPid && u.k === 'villager' && this.selected.has(u.id))
      .map((u) => u.id);
    valid.forEach((c, i) => {
      // Round-robin: each selected villager takes every V-th foundation, so
      // a crew of N raises N segments at once. Over-subscribed villagers
      // finish on their last segment; earlier ones wait as foundations
      // (right-click them with villagers to resume).
      const ids = vills.length > 0 ? [vills[i % vills.length]] : this.selIds();
      this.socket.cmd({ a: 'build', ids, kind: 'wall', x: c.x, z: c.z });
    });
    sfx.build();
    this.hud.toast(valid.length > 1 ? `🧱 ${valid.length}× wall line ordered` : '🧱 Wall ordered');
    if (!keepPlacing) this.setPlacement(null);
  }

  // ---- orders ----

  private selIds(): number[] {
    return [...this.selected];
  }

  private train(kind: string): void {
    const bid = this.selectedBld;
    if (bid == null) return;
    const s = this.latest();
    const b = s?.bd.find((x) => x.id === bid) ?? null;
    const me = this.me();
    if (!b || !me) return;
    // Mirror the server's training gates so a doomed order explains itself
    // instead of failing silently (pop cap, funds, locked elites, queue).
    const unlocked = (kind === 'villager' && b.k === 'townCenter')
      || ((kind === 'swordsman' || kind === 'archer') && b.k === 'barracks')
      || (kind === 'spearman' && b.k === 'barracks' && (b.lv ?? 0) >= 1)
      || (kind === 'rajput' && b.k === 'barracks' && (b.lv ?? 0) >= 2);
    if (!unlocked || b.p < 1) {
      this.hud.toast(kind === 'spearman' ? '🔒 Spearmen need the Armory (barracks L1).'
        : kind === 'rajput' ? '🔒 Rajputs need the Royal Akhara (barracks L2).'
        : '⏳ Building not ready.');
      sfx.error();
      return;
    }
    const queued = b.q?.length ?? 0;
    if (queued >= 5) {
      this.hud.toast('🎓 Training queue is full — wait for units to finish.');
      sfx.error();
      return;
    }
    if (me.p + queued >= me.c) {
      this.hud.toast('⛺ Population capped — build more houses.');
      sfx.error();
      return;
    }
    const cost = TRAIN_COSTS[kind];
    if (cost) {
      const lacking: string[] = [];
      if (me.w < cost.w) lacking.push(`${Math.ceil(cost.w - me.w)}🪵`);
      if (me.g < cost.g) lacking.push(`${Math.ceil(cost.g - me.g)}💰`);
      if (me.f < cost.f) lacking.push(`${Math.ceil(cost.f - me.f)}🍎`);
      if (lacking.length > 0) {
        this.hud.toast(`Need ${lacking.join(' ')} more to train.`);
        sfx.error();
        return;
      }
    }
    this.socket.cmd({ a: 'train', bid, kind });
    sfx.train();
  }

  private upgradeTower(): void {
    const bid = this.selectedBld;
    if (bid == null) return;
    const s = this.latest();
    const b = s?.bd.find((x) => x.id === bid) ?? null;
    const me = this.me();
    if (!b || !me) return;
    // Resolve the next tier locally so a doomed order explains itself
    // instead of failing silently on the server.
    const next = b.k === 'tower'
      ? TOWER_LEVELS[Math.min((b.lv ?? 0) + 1, TOWER_LEVELS.length - 1)]
      : b.k === 'barracks'
        ? BARRACKS_LEVELS[Math.min((b.lv ?? 0) + 1, BARRACKS_LEVELS.length - 1)]
        : b.k === 'wall' || b.k === 'gate'
          ? WALL_LEVELS[Math.min((b.lv ?? 0) + 1, WALL_LEVELS.length - 1)]
          : null;
    if (!next || (b.k === 'tower' && (b.lv ?? 0) >= TOWER_LEVELS.length - 1) ||
      (b.k === 'barracks' && (b.lv ?? 0) >= BARRACKS_LEVELS.length - 1) ||
      ((b.k === 'wall' || b.k === 'gate') && (b.lv ?? 0) >= WALL_LEVELS.length - 1)) {
      this.hud.toast('⬆️ Already at max tier.');
      return;
    }
    if ((b.ut ?? 0) > 0) {
      this.hud.toast(`⏳ ${next.name} already researching…`);
      return;
    }
    const lacking: string[] = [];
    if (me.w < next.w) lacking.push(`${Math.ceil(next.w - me.w)}🪵`);
    if (me.g < next.g) lacking.push(`${Math.ceil(next.g - me.g)}💰`);
    if (lacking.length > 0) {
      this.hud.toast(`Need ${lacking.join(' ')} more for ${next.name}.`);
      sfx.error();
      return;
    }
    this.socket.cmd({ a: 'upgrade', bid });
    sfx.build();
  }

  // Swing the selected finished wall into an auto-door gate (30🪵, instant).
  // Friendlies walk through; enemies face a sealed footprint.
  private convertWall(): void {
    const bid = this.selectedBld;
    if (bid == null) return;
    const s = this.latest();
    const b = s?.bd.find((x) => x.id === bid) ?? null;
    const me = this.me();
    if (!b || !me) return;
    if (b.o !== this.myPid || b.k !== 'wall' || b.p < 1) {
      this.hud.toast('🛡️ Select a finished wall of yours to gate it.');
      sfx.error();
      return;
    }
    if ((b.ut ?? 0) > 0) {
      this.hud.toast('⏳ Finish the stone research first…');
      return;
    }
    if (me.w < 30) {
      this.hud.toast(`Need ${Math.ceil(30 - me.w)}🪵 more for the gate.`);
      sfx.error();
      return;
    }
    this.socket.cmd({ a: 'convert', bid });
    sfx.build();
    this.hud.toast('🛡️ Gate ordered — friendlies pass, enemies knock.');
  }

  // Bottom-bar action for an inspected resource: send up to 5 idle villagers
  // working near the node to gather from it.
  private gatherSelectedNode(): void {
    const s = this.latest();
    const n = s?.nd.find((x) => x.id === this.selectedNode) ?? null;
    if (!s || !n) return;
    const near = s.u
      .filter((u) => u.o === this.myPid && u.k === 'villager' && u.st === 'idle')
      .map((u) => ({ u, d: Math.hypot(u.x - n.x, u.z - n.z) }))
      .filter((e) => e.d < 40)
      .sort((a, b) => a.d - b.d)
      .slice(0, 5)
      .map((e) => e.u.id);
    if (near.length === 0) {
      this.hud.toast('💤 No idle villagers nearby — select workers, then right-click to gather.');
      sfx.error();
      return;
    }
    this.socket.cmd({ a: 'gather', ids: near, tid: n.id });
    this.markers.ping(n.x, n.z, resColor(n.k));
    sfx.gather();
    this.hud.toast(`🧑‍🌾 ${near.length}× gathering`);
  }

  // Bottom-bar quick-select: all / idle / 5-nearest villagers.
  private selectVillagers(scope: string): void {
    const s = this.latest();
    if (!s) return;
    let vills = s.u.filter((u) => u.o === this.myPid && u.k === 'villager');
    if (scope === 'idle') vills = vills.filter((u) => u.st === 'idle');
    if (scope === 'few') {
      vills = vills
        .map((u) => ({ u, d: Math.hypot(u.x - this.renderer.camX, u.z - this.renderer.camZ) }))
        .sort((a, b) => a.d - b.d)
        .slice(0, 5)
        .map((e) => e.u);
    }
    this.selected.clear();
    this.selectedBld = null;
    this.selectedNode = null;
    this.enemySel = null;
    for (const v of vills) this.selected.add(v.id);
    if (vills.length > 0) sfx.select();
    else if (scope === 'idle') this.hud.toast('💤 No idle villagers right now.');
    else this.hud.toast('🧑‍🌾 No villagers — train more at the Town Center.');
    this.refreshPanel();
  }

  // Bottom-bar quick-select: all soldiers or one kind ('swordsman', …).
  private selectArmy(kind: string): void {
    const s = this.latest();
    if (!s) return;
    const army = s.u.filter(
      (u) => u.o === this.myPid && u.k !== 'villager' && (kind === 'all' || u.k === kind),
    );
    this.selected.clear();
    this.selectedBld = null;
    this.selectedNode = null;
    this.enemySel = null;
    for (const u of army) this.selected.add(u.id);
    if (army.length > 0) sfx.select();
    else this.hud.toast('⚔️ No soldiers yet — train them at the Barracks.');
    this.refreshPanel();
  }

  private stop(): void {
    const ids = this.selIds();
    if (ids.length) this.socket.cmd({ a: 'stop', ids });
    sfx.click();
  }

  private orderAt(x: number, z: number, cx: number, cy: number): void {
    const s = this.latest();
    if (!s) return;
    const ids = this.selIds();
    if (ids.length === 0) return;
    const sel = ids
      .map((id) => s.u.find((u) => u.id === id))
      .filter((u): u is Unit => !!u);
    const hasVill = sel.some((u) => u.k === 'villager');
    const hasArmy = sel.some((u) => u.k !== 'villager');

    // Enemy unit/building under cursor?
    const foe = this.pickFoe(s, x, z);
    if (foe && hasArmy) {
      const armyIds = sel.filter((u) => u.k !== 'villager').map((u) => u.id);
      if (this.amoveArmed) {
        this.socket.cmd({ a: 'amove', ids: armyIds, x, z });
        this.markers.ping(x, z, '#fb923c');
      } else {
        this.socket.cmd({ a: 'attack', ids: armyIds, tid: foe.id, tb: foe.bld });
        this.markers.ping(foe.x, foe.z, '#ef4444');
      }
      sfx.attack();
      this.amoveArmed = false;
      this.refreshPanel();
      return;
    }
    // Own building under cursor? Resume unfinished work, or repair finished
    // but damaged structures (villagers only).
    const ownBld = this.pickOwnBld(s, x, z);
    if (ownBld && hasVill && (ownBld.p < 1 || ownBld.hp < ownBld.m)) {
      const builders = sel.filter((u) => u.k === 'villager').map((u) => u.id);
      this.socket.cmd({ a: 'build', ids: builders, bid: ownBld.id });
      this.markers.ping(ownBld.x, ownBld.z, '#4ade80');
      sfx.build();
      this.amoveArmed = false;
      this.refreshPanel();
      return;
    }
    // Own finished farm? Work the crops (endless food).
    if (ownBld && ownBld.k === 'farm' && ownBld.p >= 1 && hasVill) {
      const farmers = sel.filter((u) => u.k === 'villager').map((u) => u.id);
      this.socket.cmd({ a: 'gather', ids: farmers, tid: ownBld.id, tb: true });
      this.markers.ping(ownBld.x, ownBld.z, resColor('food'));
      sfx.gather();
      this.amoveArmed = false;
      this.refreshPanel();
      return;
    }
    // Resource node under cursor? Screen-space pick: clicking the visible
    // foliage must assign the trunk (a ground raycast lands behind it).
    const node = this.pickNodeAt(s, cx, cy);
    if (node && hasVill) {
      const villIds = sel.filter((u) => u.k === 'villager').map((u) => u.id);
      this.socket.cmd({ a: 'gather', ids: villIds, tid: node.id });
      this.markers.ping(node.x, node.z, resColor(node.k));
      sfx.gather();
      this.amoveArmed = false;
      this.refreshPanel();
      return;
    }
    // Plain ground: move (or attack-move if armed).
    this.socket.cmd({ a: this.amoveArmed ? 'amove' : 'move', ids, x, z });
    this.markers.ping(x, z, '#e5e7eb');
    if (this.amoveArmed) sfx.attack();
    else sfx.move();
    this.amoveArmed = false;
    this.refreshPanel();
  }

  private pickOwn(s: Snapshot, x: number, z: number): Unit | null {
    let best: Unit | null = null;
    let bestD = 1.6;
    for (const u of s.u) {
      if (u.o !== this.myPid) continue;
      const d = Math.hypot(u.x - x, u.z - z);
      if (d < bestD) {
        bestD = d;
        best = u;
      }
    }
    return best;
  }

  private pickOwnBld(s: Snapshot, x: number, z: number): Building | null {
    for (const b of s.bd) {
      if (b.o !== this.myPid) continue;
      const r = bldSize(b.k) / 2 + 0.6;
      if (Math.abs(b.x - x) < r && Math.abs(b.z - z) < r) return b;
    }
    return null;
  }

  private pickFoe(s: Snapshot, x: number, z: number): { id: number; bld: boolean; x: number; z: number } | null {
    for (const u of s.u) {
      if (u.o === this.myPid) continue;
      if (Math.hypot(u.x - x, u.z - z) < 1.6) return { id: u.id, bld: false, x: u.x, z: u.z };
    }
    for (const b of s.bd) {
      if (b.o === this.myPid) continue;
      const r = bldSize(b.k) / 2 + 0.8;
      if (Math.abs(b.x - x) < r && Math.abs(b.z - z) < r) return { id: b.id, bld: true, x: b.x, z: b.z };
    }
    return null;
  }

  private pickNode(s: Snapshot, x: number, z: number): { id: number; k: string; x: number; z: number } | null {
    let best: { id: number; k: string; x: number; z: number } | null = null;
    let bestD = 2.4;
    for (const n of s.nd) {
      const d = Math.hypot(n.x - x, n.z - z);
      if (d < bestD) {
        bestD = d;
        best = { id: n.id, k: n.k, x: n.x, z: n.z };
      }
    }
    return best;
  }

  // Screen-space resource pick: project each node anchor to pixels and take
  // the nearest within 36px, so clicking a tall canopy assigns its trunk.
  // Falls back to the ground-plane radius when nothing is near the cursor.
  private pickNodeAt(s: Snapshot, cx: number, cy: number): { id: number; k: string; x: number; z: number } | null {
    const rect = this.renderer.dom.getBoundingClientRect();
    const cam = this.renderer.camera;
    let best: { id: number; k: string; x: number; z: number } | null = null;
    let bestPx = 36;
    for (const n of s.nd) {
      this.proj.set(n.x, 1.2, n.z).project(cam);
      if (this.proj.z > 1) continue; // behind camera
      const px = rect.left + ((this.proj.x + 1) / 2) * rect.width;
      const py = rect.top + ((1 - this.proj.y) / 2) * rect.height;
      const d = Math.hypot(px - cx, py - cy);
      if (d < bestPx) {
        bestPx = d;
        best = { id: n.id, k: n.k, x: n.x, z: n.z };
      }
    }
    if (best) return best;
    const w = this.renderer.screenToWorld(cx, cy);
    return w ? this.pickNode(s, w.x, w.z) : null;
  }

  // ---- input ----

  private attachInput(container: HTMLElement): void {
    const cv = this.renderer.dom;
    cv.addEventListener('contextmenu', (e) => e.preventDefault());

    // AoE-style edge pan: track the cursor while it is over the canvas.
    // (Hovering HUD panels/minimap means the cursor is NOT on the canvas, so
    // those corners never trigger a pan.)
    cv.addEventListener('mousemove', (e) => {
      const r = cv.getBoundingClientRect();
      this.renderer.pointerEdge = {
        x: e.clientX - r.left,
        y: e.clientY - r.top,
      };
      // Glide the placement ghost under the cursor with live validity.
      // Walls preview at their snapped (edge-adjacent) cell — or as a full
      // rampart line while dragging.
      if (this.ghost && this.placement) {
        const w = this.renderer.screenToWorld(e.clientX, e.clientY);
        const s = this.latest();
        if (w && s) {
          if (this.placement === 'wall' && this.wallDrag) {
            const cells = this.wallLineCells(this.wallDrag.wx, this.wallDrag.wz, w.x, w.z, s);
            this.ghost.visible = false;
            this.updateWallLinePreview(cells, s);
          } else {
            const p = this.snapWall(this.placement, w.x, w.z, s);
            if (this.placement === 'wall') {
              // Corner-aware ghost: rebake only when connectivity flips.
              const walls = s.bd.filter((b) => b.k === 'wall' || b.k === 'gate');
              this.refreshGhost('wall', wallMask(p.x, p.z, walls, this.myPid));
              if (this.ghost) this.ghost.rotation.y = 0;
            }
            if (this.ghost) {
              this.ghost.visible = true;
              this.ghost.position.set(p.x, 0, p.z);
              this.buildings.setGhostValid(this.ghost, this.placeBlockReason(this.placement, p.x, p.z, s) === null);
            }
          }
        } else {
          this.ghost.visible = false;
        }
      }
      // Resource hover preview: with villagers selected, ring the node a
      // right-click would assign (green wood / yellow gold / pink food).
      // Throttled to ~16Hz: pickNodeAt projects every node to screen and
      // was the main mousemove jank source.
      if (this.playing && !this.over && !this.placement && this.selHasVill) {
        const nowMs = performance.now();
        if (nowMs - this.hoverLast >= 60) {
          this.hoverLast = nowMs;
          const s = this.latest();
          const n = s ? this.pickNodeAt(s, e.clientX, e.clientY) : null;
          if (n) {
            this.markers.setHover(n.x, n.z, resColor(n.k));
          } else {
            // Finished own farm under cursor? Ring it pink for crop duty.
            const w = this.renderer.screenToWorld(e.clientX, e.clientY);
            const f = w && s ? this.pickOwnBld(s, w.x, w.z) : null;
            if (f && f.k === 'farm' && f.p >= 1) this.markers.setHover(f.x, f.z, resColor('food'));
            else this.markers.clearHover();
          }
        }
      } else {
        this.markers.clearHover();
      }
    });
    cv.addEventListener('mouseleave', () => {
      this.renderer.pointerEdge = null;
      this.markers.clearHover();
    if (this.ghost) this.ghost.visible = false;
    this.markers.clearHover();
    });
    window.addEventListener('blur', () => {
      this.renderer.pointerEdge = null;
      this.renderer.keys.clear();
      // A drag spanning an alt-tab leaves a stale anchor: drop the preview
      // but stay in placement mode.
      if (this.wallDrag) this.clearWallLine();
    });
    // Fullscreen + mute shortcuts — work everywhere, even in the lobby.
    window.addEventListener('keydown', (e) => {
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      if (e.key.toLowerCase() === 'f') this.toggleFullscreen();
      if (e.key.toLowerCase() === 'm') {
        sfx.toggleMute();
        this.hud.syncMuteIcon();
      }
    });

    cv.addEventListener('mousedown', (e) => {
      if (!this.playing || this.over) return;
      if (e.button === 1) {
        this.panning = { sx: e.clientX, sy: e.clientY };
        this.renderer.edgePaused = true; // middle-drag owns the camera
        e.preventDefault();
        return;
      }
      if (e.button !== 0) return;
      const w = this.renderer.screenToWorld(e.clientX, e.clientY);
      if (!w) return;
      if (this.placement === 'wall') {
        // Rampart drag: click anchors the line start, release builds the
        // whole row (a tap without dragging places a single segment).
        this.wallDrag = { sx: e.clientX, sy: e.clientY, wx: w.x, wz: w.z };
        if (this.ghost) this.ghost.visible = false;
        return;
      }
      if (this.placement) {
        const s = this.latest();
        const p = s ? this.snapWall(this.placement, w.x, w.z, s) : w;
        const reason = s ? this.placeBlockReason(this.placement, p.x, p.z, s) : null;
        if (reason) {
          this.hud.toast(reason); // stay in placement mode, adjust and retry
          sfx.error();
          return;
        }
        this.socket.cmd({ a: 'build', ids: this.selIds(), kind: this.placement, x: p.x, z: p.z });
        sfx.build();
        if (!e.shiftKey) this.setPlacement(null);
        return;
      }
      if (this.amoveArmed) {
        this.orderAt(w.x, w.z, e.clientX, e.clientY);
        return;
      }
      this.boxStart = { sx: e.clientX, sy: e.clientY };
    });

    window.addEventListener('mousemove', (e) => {
      if (this.panning) {
        const k = this.renderer.pixelsToWorld();
        this.renderer.panBy(-(e.clientX - this.panning.sx) * k, -(e.clientY - this.panning.sy) * k);
        this.panning = { sx: e.clientX, sy: e.clientY };
        return;
      }
      if (!this.boxStart) return;
      const r = container.getBoundingClientRect();
      const x0 = Math.min(this.boxStart.sx, e.clientX) - r.left;
      const y0 = Math.min(this.boxStart.sy, e.clientY) - r.top;
      const x1 = Math.max(this.boxStart.sx, e.clientX) - r.left;
      const y1 = Math.max(this.boxStart.sy, e.clientY) - r.top;
      Object.assign(this.boxEl.style, {
        display: 'block',
        left: `${x0}px`,
        top: `${y0}px`,
        width: `${x1 - x0}px`,
        height: `${y1 - y0}px`,
      });
      cv.classList.add('selecting');
    });

    window.addEventListener('mouseup', (e) => {
      if (this.panning && e.button === 1) {
        this.panning = null;
        this.renderer.edgePaused = false;
        return;
      }
      // Wall rampart drag release: build the whole line (a tap without a
      // drag lands a single segment through the same path).
      if (this.wallDrag && e.button === 0 && this.placement === 'wall') {
        const drag = this.wallDrag;
        this.wallDrag = null;
        for (const g of this.wallGhosts) g.visible = false;
        if (!this.playing || this.over) return;
        const s = this.latest();
        const w = this.renderer.screenToWorld(e.clientX, e.clientY);
        if (s && w) {
          const cells = this.wallLineCells(drag.wx, drag.wz, w.x, w.z, s);
          this.commitWallLine(cells, s, e.shiftKey);
        }
        return;
      }
      if (!this.boxStart || e.button !== 0) return;
      const dx = e.clientX - this.boxStart.sx;
      const dy = e.clientY - this.boxStart.sy;
      this.boxEl.style.display = 'none';
      cv.classList.remove('selecting');
      this.boxStart = null;
      if (!this.playing || this.over) return;
      if (Math.hypot(dx, dy) < 6) {
        this.clickSelect(e.clientX, e.clientY, e.shiftKey);
      } else {
        this.boxSelect(this.boxEl, e.shiftKey);
      }
    });

    cv.addEventListener('dblclick', (e) => {
      if (!this.playing || this.over) return;
      const s = this.latest();
      const w = this.renderer.screenToWorld(e.clientX, e.clientY);
      if (!s || !w) return;
      const seed = this.pickOwn(s, w.x, w.z);
      if (!seed) return;
      this.selected.clear();
      for (const u of s.u) {
        if (u.o !== this.myPid || u.k !== seed.k) continue;
        if (Math.hypot(u.x - this.renderer.camX, u.z - this.renderer.camZ) < 45) {
          this.selected.add(u.id);
        }
      }
      this.selectedBld = null;
      this.selectedNode = null;
      this.enemySel = null;
      if (this.selected.size > 0) sfx.select();
      this.refreshPanel();
    });

    cv.addEventListener('mouseup', (e) => {
      if (e.button !== 2 || !this.playing || this.over) return;
      if (this.placement || this.amoveArmed) {
        this.amoveArmed = false;
        this.setPlacement(null);
        return;
      }
      const w = this.renderer.screenToWorld(e.clientX, e.clientY);
      if (w) this.orderAt(w.x, w.z, e.clientX, e.clientY);
    });

    cv.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.renderer.zoom(e.deltaY > 0 ? 1.1 : 0.9);
    }, { passive: false });

    window.addEventListener('keydown', (e) => {
      if (!this.playing || this.over) return;
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      const k = e.key.toLowerCase();
      if (k === 'escape') {
        if (this.hud.isHelpOpen()) this.hud.toggleHelp();
        this.amoveArmed = false;
        this.setPlacement(null);
      } else if (k === 'a') {
        this.amoveArmed = !this.amoveArmed;
        this.setPlacement(null);
      } else if (k === 's') {
        this.stop();
      } else if (k === 'q') {
        this.renderer.rotate(0.12);
      } else if (k === 'e') {
        this.renderer.rotate(-0.12);
      } else if (k === 'h' || k === 'g' || k === 'r' || k === 't' || k === 'y') {
        const s = this.latest();
        const selBld = s?.bd.find((x) => x.id === this.selectedBld) ?? null;
        // G with an own finished wall selected swings a gate (farm placement
        // needs villagers selected, which never coincides with a building).
        if (k === 'g' && selBld && selBld.o === this.myPid && selBld.k === 'wall' && selBld.p >= 1) {
          this.convertWall();
          return;
        }
        const kind = k === 'h' ? 'house' : k === 'g' ? 'farm' : k === 'r' ? 'barracks' : k === 't' ? 'tower' : 'wall';
        const hasVill = s?.u.some((u) => this.selected.has(u.id) && u.k === 'villager' && u.o === this.myPid);
        if (hasVill) {
          this.amoveArmed = false;
          this.setPlacement(this.placement === kind ? null : kind);
        }
      } else if (k === '1' || k === '2' || k === '3' || k === '4') {
        const s = this.latest();
        const b = s?.bd.find((x) => x.id === this.selectedBld);
        if (b && b.o === this.myPid && b.p >= 1) {
          if (b.k === 'townCenter' && k === '1') this.train('villager');
          if (b.k === 'barracks' && k === '1') this.train('swordsman');
          if (b.k === 'barracks' && k === '2') this.train('archer');
          if (b.k === 'barracks' && k === '3' && (b.lv ?? 0) >= 1) this.train('spearman');
          if (b.k === 'barracks' && k === '4' && (b.lv ?? 0) >= 2) this.train('rajput');
        }
      } else if (k === 'u') {
        const s = this.latest();
        const b = s?.bd.find((x) => x.id === this.selectedBld);
        if (b && b.o === this.myPid && (b.k === 'tower' || b.k === 'barracks' || b.k === 'wall' || b.k === 'gate') && b.p >= 1) {
          this.upgradeTower();
        }
      } else if (k === '.') {
        this.selectVillagers('idle');
      }
    });
  }

  private clickSelect(cx: number, cy: number, additive: boolean): void {
    const s = this.latest();
    const w = this.renderer.screenToWorld(cx, cy);
    if (!s || !w) return;
    const u = this.pickOwn(s, w.x, w.z);
    if (u) {
      if (!additive) {
        this.selected.clear();
        this.selectedBld = null;
        this.selectedNode = null;
        this.enemySel = null;
      }
      if (this.selected.has(u.id)) this.selected.delete(u.id);
      else this.selected.add(u.id);
      if (this.selected.size > 0) sfx.select();
      this.refreshPanel();
      return;
    }
    const b = this.pickOwnBld(s, w.x, w.z);
    if (b) {
      this.selected.clear();
      this.selectedBld = b.id;
      this.selectedNode = null;
      this.enemySel = null;
      sfx.select();
      this.refreshPanel();
      return;
    }
    const foe = this.pickFoe(s, w.x, w.z);
    if (foe) {
      this.selected.clear();
      this.selectedBld = null;
      this.selectedNode = null;
      this.enemySel = foe;
      this.refreshPanel();
      return;
    }
    // Resource node under cursor? Inspect it in the bottom bar (screen-space
    // pick so clicking a tall canopy selects its trunk).
    const nd = this.pickNodeAt(s, cx, cy);
    if (nd) {
      this.selected.clear();
      this.selectedBld = null;
      this.selectedNode = nd.id;
      this.enemySel = null;
      sfx.select();
      this.refreshPanel();
      return;
    }
    if (!additive) {
      this.selected.clear();
      this.selectedBld = null;
      this.selectedNode = null;
      this.enemySel = null;
      this.refreshPanel();
    }
  }

  private boxSelect(box: HTMLDivElement, additive: boolean): void {
    const s = this.latest();
    if (!s) return;
    // Project every own unit to screen space and test against the box.
    const rect = this.renderer.dom.getBoundingClientRect();
    const bx0 = parseFloat(box.style.left) + rect.left;
    const by0 = parseFloat(box.style.top) + rect.top;
    const bx1 = bx0 + parseFloat(box.style.width);
    const by1 = by0 + parseFloat(box.style.height);
    if (!additive) {
      this.selected.clear();
      this.selectedBld = null;
      this.selectedNode = null;
      this.enemySel = null;
    }
    const cam = this.renderer.camera;
    const vec = this.proj;
    for (const u of s.u) {
      if (u.o !== this.myPid) continue;
      vec.set(u.x, 0.6, u.z).project(cam);
      if (vec.z > 1) continue; // behind camera
      const px = rect.left + ((vec.x + 1) / 2) * rect.width;
      const py = rect.top + ((1 - vec.y) / 2) * rect.height;
      if (px >= bx0 && px <= bx1 && py >= by0 && py <= by1) this.selected.add(u.id);
    }
    if (this.selected.size > 0) sfx.select();
    this.refreshPanel();
  }
}
